import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import XalucaOriginalsBadge from "@/components/XalucaOriginalsBadge";

// Horizontal, snap-scroll carousel for the *secondary* Xaluca Originals
// experiences. Touch swipe and trackpad swipe both work natively because
// the inner rail is just an overflow-x scroll container with scroll-snap.
// The arrow buttons scroll the rail by one card width on click.

const pick = (obj, lang) => (typeof obj === "string" ? obj : obj?.[lang] || obj?.en || "");

// Per-experience testimonial content. Rendered inline at the bottom of
// each carousel card so the social proof feels part of the card — no
// jump to a separate "reviews" page required. Localised (fr/en/es) so
// it stays in sync with the visitor's chosen language.
//
// Keys mirror the `key` field of every `EXPERIENCES` entry in
// `/lib/data.js` that carries `secondary: true`. Falling back to a
// generic Xaluca Originals quote keeps the layout intact if a new
// secondary experience is added to the data without a matching
// testimonial (or if the CMS injects a novel key at runtime).
const TESTIMONIALS = {
  "sunrise-camel-trek": {
    quote: {
      en: "The dunes turned pink under our feet — a quiet, unforgettable morning.",
      fr: "Les dunes ont viré au rose sous nos pieds — un matin calme, inoubliable.",
      es: "Las dunas se volvieron rosas bajo nuestros pies — una mañana silenciosa, inolvidable.",
    },
    author: "Lucía M.",
    origin: { en: "Barcelona", fr: "Barcelone", es: "Barcelona" },
    avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=96&h=96&q=80",
  },
  "sunset-camel-trek": {
    quote: {
      en: "We rode into a sky of copper and honey. No filter comes close.",
      fr: "Nous avons chevauché dans un ciel de cuivre et de miel. Aucun filtre n'y arrive.",
      es: "Cabalgamos en un cielo de cobre y miel. Ningún filtro se le acerca.",
    },
    author: "Thomas K.",
    origin: { en: "Berlin", fr: "Berlin", es: "Berlín" },
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=96&h=96&q=80",
  },
  "arfoud-pizza-night": {
    quote: {
      en: "A wood-fired oven under the stars — the crust, the mint tea, the desert. Perfect.",
      fr: "Un four à bois sous les étoiles — la pâte, le thé à la menthe, le désert. Parfait.",
      es: "Un horno de leña bajo las estrellas — la masa, el té con menta, el desierto. Perfecto.",
    },
    author: "Isabel R.",
    origin: { en: "Madrid", fr: "Madrid", es: "Madrid" },
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=96&h=96&q=80",
  },
  "quad-erg-chebbi-2h": {
    quote: {
      en: "Two hours flying over the crests of Erg Chebbi. Pure adrenaline, expertly guided.",
      fr: "Deux heures à voler sur les crêtes de l'Erg Chebbi. Adrénaline pure, guidage expert.",
      es: "Dos horas volando sobre las crestas del Erg Chebbi. Pura adrenalina, guía experta.",
    },
    author: "Marc D.",
    origin: { en: "Lyon", fr: "Lyon", es: "Lyon" },
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=96&h=96&q=80",
  },
  "quad-erg-chebbi-4h": {
    quote: {
      en: "Four hours deep into the erg, ending at a hidden oasis. Bucket-list stuff.",
      fr: "Quatre heures au fond de l'erg, jusqu'à une oasis cachée. Un rêve qui se coche.",
      es: "Cuatro horas al fondo del erg, hasta un oasis escondido. Cosa de lista de deseos.",
    },
    author: "Chloé P.",
    origin: { en: "Paris", fr: "Paris", es: "París" },
    avatar: "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=96&h=96&q=80",
  },
};

const FALLBACK_TESTIMONIAL = {
  quote: {
    en: "Xaluca Originals turn a trip into a story you'll tell for years.",
    fr: "Les Xaluca Originals transforment un voyage en une histoire à raconter pendant des années.",
    es: "Los Xaluca Originals convierten un viaje en una historia que contarás durante años.",
  },
  author: "Grup Xaluca",
  origin: { en: "Guest reviews", fr: "Avis clients", es: "Opiniones de huéspedes" },
  avatar: null,
};

const XalucaOriginalsCarousel = ({ items = [], lang, variant = "light" }) => {
  const railRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const refreshArrows = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    setCanPrev(rail.scrollLeft > 8);
    setCanNext(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 8);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    refreshArrows();
    rail.addEventListener("scroll", refreshArrows, { passive: true });
    window.addEventListener("resize", refreshArrows);
    return () => {
      rail.removeEventListener("scroll", refreshArrows);
      window.removeEventListener("resize", refreshArrows);
    };
  }, [refreshArrows, items.length]);

  const scrollByCards = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector("[data-carousel-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : rail.clientWidth * 0.85;
    rail.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!items.length) return null;

  const discover =
    lang === "fr" ? "Découvrir" : lang === "es" ? "Descubrir" : "Discover";

  // Palette switches so the same carousel reads correctly on both the
  // cream `/experiences` background and the new black homepage panel.
  const isDark = variant === "dark";
  const arrowCls = isDark
    ? "bg-[#1A1513] border-[#FDFBF7]/15 hover:border-[#D4A373] text-[#FDFBF7] hover:text-[#D4A373]"
    : "bg-[#FDFBF7] border-[#2C2621]/15 hover:border-[#C16542] text-[#2C2621] hover:text-[#C16542]";
  const arrowDisabledCls = isDark
    ? "bg-[#1A1513] border-[#FDFBF7]/8 text-[#FDFBF7]/25 cursor-not-allowed"
    : "bg-[#FDFBF7] border-[#2C2621]/8 text-[#2C2621]/25 cursor-not-allowed";
  // Subtle premium shadow per variant — blends with the background
  // instead of glowing white. Dark cards lift on a deeper ambient
  // shadow; cream cards use a soft warm-grey shadow.
  const cardCls = isDark
    ? "bg-[#1A1513] border-[#FDFBF7]/12 hover:border-[#D4A373] shadow-[0_18px_40px_-22px_rgba(0,0,0,0.85)] hover:shadow-[0_26px_50px_-22px_rgba(0,0,0,0.95)] transition-[border-color,box-shadow,transform] duration-300"
    : "bg-[#FDFBF7] border-[#E8DFCF] hover:border-[#D4A373] shadow-[0_10px_24px_-14px_rgba(44,38,33,0.18)] hover:shadow-[0_18px_36px_-14px_rgba(44,38,33,0.25)] transition-[border-color,box-shadow,transform] duration-300";
  const titleCls = isDark
    ? "text-[#FDFBF7] hover:!text-[#D4A373]"
    : "text-[#2C2621] hover:text-[#C16542]";
  const titleColorCls = isDark ? "text-[#FDFBF7]" : "text-[#2C2621]";
  const descCls = isDark ? "text-[#FDFBF7]/65" : "text-[#5C5248]";
  const discoverCls = isDark
    ? "text-[#D4A373] hover:text-[#FDFBF7] border-[#D4A373]/35 hover:border-[#FDFBF7]"
    : "text-[#C16542] hover:text-[#A35133] border-[#C16542]/30 hover:border-[#A35133]";

  return (
    <div className="relative" data-testid="xaluca-originals-carousel">
      {/* Arrow buttons — hidden on mobile (touch-first); enabled on tablet+ */}
      <button
        type="button"
        onClick={() => scrollByCards(-1)}
        aria-label="Previous experiences"
        disabled={!canPrev}
        className={`hidden md:flex absolute -left-2 lg:-left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition-all ${
          canPrev ? arrowCls : arrowDisabledCls
        }`}
        data-testid="xaluca-originals-prev"
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={1.6} />
      </button>
      <button
        type="button"
        onClick={() => scrollByCards(1)}
        aria-label="Next experiences"
        disabled={!canNext}
        className={`hidden md:flex absolute -right-2 lg:-right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition-all ${
          canNext ? arrowCls : arrowDisabledCls
        }`}
        data-testid="xaluca-originals-next"
      >
        <ChevronRight className="w-5 h-5" strokeWidth={1.6} />
      </button>

      {/* Scroll-snap rail. Mobile users swipe; desktop users use arrows. */}
      <ul
        ref={railRef}
        className="flex gap-5 md:gap-6 overflow-x-auto pb-4 -mb-4 snap-x snap-mandatory scroll-px-6 md:scroll-px-12 px-1 md:px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="xaluca-originals-rail"
      >
        {items.map((ex) => (
          <li
            key={`carousel-${ex.key}`}
            data-carousel-card
            className="snap-start shrink-0 w-[78vw] sm:w-[58vw] md:w-[44vw] lg:w-[28rem] xl:w-[26rem]"
            data-testid={`xaluca-original-card-${ex.key}`}
            data-cms-scope={`xaluca-original.${ex.key}`}
          >
            <article className={`group h-full flex flex-col border transition-colors duration-300 overflow-hidden ${cardCls}`}>
              <Link
                to={`/experiences/${ex.key}`}
                className="block relative aspect-[16/10] overflow-hidden"
                data-testid={`xaluca-original-link-${ex.key}`}
              >
                <img
                  src={ex.image}
                  alt={pick(ex.title, lang)}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  data-cms-role="card.image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/45 via-transparent to-transparent opacity-80" />
                <XalucaOriginalsBadge
                  variant="dark"
                  className="absolute top-3 left-3"
                  testid={`xaluca-original-card-${ex.key}-badge`}
                />
              </Link>
              <div className="p-5 md:p-6 flex-1 flex flex-col">
                <h3 className={`font-serif-x text-lg md:text-xl leading-snug tracking-tight ${titleColorCls}`}>
                  <Link
                    to={`/experiences/${ex.key}`}
                    className={`transition-colors ${titleCls}`}
                  >
                    {pick(ex.title, lang)}
                  </Link>
                </h3>
                <p className={`mt-3 text-sm leading-relaxed line-clamp-3 flex-1 ${descCls}`}>
                  {pick(ex.desc, lang)}
                </p>
                <Link
                  to={`/experiences/${ex.key}`}
                  className={`mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b pb-1 w-fit transition-colors ${discoverCls}`}
                  data-testid={`xaluca-original-discover-${ex.key}`}
                >
                  {discover}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Link>
                {/* Testimonial — per-experience social proof anchored
                    directly to the card. Uses a subtle amber top rule
                    so it reads as a discrete "section" of the card
                    without breaking the editorial feel. */}
                {(() => {
                  const t = TESTIMONIALS[ex.key] || FALLBACK_TESTIMONIAL;
                  const originText = pick(t.origin, lang);
                  return (
                    <figure
                      className={`mt-6 pt-5 border-t ${isDark ? "border-[#FDFBF7]/12" : "border-[#2C2621]/10"}`}
                      data-testid={`xaluca-original-testimonial-${ex.key}`}
                      data-cms-scope={`xaluca-original.${ex.key}.testimonial`}
                    >
                      <Quote
                        className={`w-4 h-4 ${isDark ? "text-[#D4A373]" : "text-[#C16542]"} mb-2`}
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                      <blockquote
                        className={`font-serif-x italic text-[13px] md:text-sm leading-relaxed ${isDark ? "text-[#FDFBF7]/85" : "text-[#2C2621]/90"}`}
                        data-cms-role="quote"
                      >
                        &ldquo;{pick(t.quote, lang)}&rdquo;
                      </blockquote>
                      <figcaption className="mt-3 flex items-center gap-2.5">
                        {t.avatar ? (
                          <img
                            src={t.avatar}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className={`w-7 h-7 rounded-full object-cover ring-[1.5px] ${isDark ? "ring-[#D4A373]/60" : "ring-[#C16542]/40"} shrink-0`}
                            data-cms-role="avatar"
                          />
                        ) : null}
                        <div className="min-w-0">
                          <p
                            className={`text-[11px] tracking-[0.18em] uppercase leading-tight ${isDark ? "text-[#FDFBF7]" : "text-[#2C2621]"} truncate`}
                            data-cms-role="author"
                          >
                            {t.author}
                          </p>
                          {originText ? (
                            <p
                              className={`text-[10px] tracking-[0.2em] uppercase leading-tight mt-0.5 ${isDark ? "text-[#D4A373]/80" : "text-[#5C5248]"} truncate`}
                              data-cms-role="origin"
                            >
                              {originText}
                            </p>
                          ) : null}
                        </div>
                      </figcaption>
                    </figure>
                  );
                })()}
              </div>
            </article>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default XalucaOriginalsCarousel;
